import { partsSellPriceRow } from "@/lib/partsPrice";
/**
 * Loader helpers that let dynamic routes answer with a real HTTP 404 instead of
 * rendering an empty "not found" body with a 200 status (Google reports those
 * as Soft 404s).
 */
import { getSupabase } from "@/integrations/supabase/lazy";

/** True when a row with this slug exists in the given table/column. */
export async function slugExists(
  table: string,
  column: string,
  slug: string,
): Promise<boolean> {
  try {
    const sb = await getSupabase();
    const { data, error } = await (sb as any)
      .from(table)
      .select(column)
      .eq(column, slug)
      .limit(1)
      .maybeSingle();
    if (error) return true; // never 404 on an infrastructure error
    return !!data;
  } catch {
    return true;
  }
}

export type PartsSlugResolution =
  | { kind: "ok" }
  | { kind: "redirect"; to: string }
  | { kind: "missing" };

/**
 * Mirrors getProduct()'s slug resolution: exact short_slug/admin_slug match,
 * then the legacy "slug without the vendor-code prefix" recovery.
 */
/** Slugs / part numbers may only contain filter-safe characters (no commas, parentheses, dots-as-operators abuse). */
export function isSafePartsKey(key: string): boolean {
  return typeof key === "string" && /^[A-Za-z0-9_~.\-]{1,200}$/.test(key);
}

export async function resolvePartsSlug(key: string): Promise<PartsSlugResolution> {
  if (!key) return { kind: "missing" };
  if (!isSafePartsKey(key)) return { kind: "missing" };
  if (/^\d+$/.test(key)) return { kind: "ok" }; // numeric ids handled by the page
  try {
    const sb = await getSupabase();
    const { data, error } = await (sb as any)
      .from("cdn_products")
      .select("short_slug")
      .or(`short_slug.eq.${key},admin_slug.eq.${key}`)
      .limit(1)
      .maybeSingle();
    if (error) return { kind: "ok" };
    if (data) {
      // One 200 URL per part: any non-canonical slug (e.g. long admin_slug)
      // 301s to the short_slug the sitemap and canonical tag declare.
      if (data.short_slug && data.short_slug !== key) {
        const { data: exact } = await (sb as any)
          .from("cdn_products")
          .select("short_slug")
          .eq("short_slug", key)
          .limit(1)
          .maybeSingle();
        if (!exact) return { kind: "redirect", to: `/parts-catalog/${data.short_slug}` };
      }
      return { kind: "ok" };
    }

    if (key.includes("-")) {
      const escaped = key.replace(/[%_,()]/g, "");
      const { data: legacy } = await (sb as any)
        .from("cdn_products")
        .select("short_slug")
        .like("short_slug", `%-${escaped}`)
        .limit(1)
        .maybeSingle();
      if (legacy?.short_slug) {
        return { kind: "redirect", to: `/parts-catalog/${legacy.short_slug}` };
      }
    }

    // Non-numeric product ids / part numbers (e.g. "F93-BT") are valid keys —
    // the cart and older links use them. Send them to the canonical slug.
    const { data: byKey } = await (sb as any)
      .from("cdn_products")
      .select("product_id, short_slug, admin_slug")
      .or(`product_id.eq.${key},product_number.eq.${key}`)
      .limit(1)
      .maybeSingle();
    if (byKey) {
      const canonical = byKey.short_slug || byKey.admin_slug;
      if (canonical) return { kind: "redirect", to: `/parts-catalog/${canonical}` };
      return { kind: "ok" };
    }

    // Slugs that were regenerated (vendor-prefix fix) keep working through the
    // recorded 301 map instead of answering 404.
    const { data: mapped } = await (sb as any)
      .from("url_redirects")
      .select("to_path")
      .eq("from_path", `/parts-catalog/${key}`)
      .eq("is_active", true)
      .limit(1)
      .maybeSingle();
    if (mapped?.to_path) return { kind: "redirect", to: mapped.to_path };
    return { kind: "missing" };
  } catch {
    return { kind: "ok" };
  }
}

/** Number of live products carried by a /producttag/<slug> hub. */
export async function countTagProducts(slug: string): Promise<number | null> {
  try {
    const sb = await getSupabase();
    const { data: match } = await (sb as any)
      .from("product_tag_slugs")
      .select("slug")
      .eq("slug", slug)
      .maybeSingle();

    if (match) {
      const { data } = await (sb as any).rpc("products_for_tag_slug", {
        _slug: slug,
        _sort: "name",
        _limit: 3,
      });
      return Array.isArray(data) ? data.length : null;
    }

    const { data: legacy } = await (sb as any)
      .from("generic_legacy_tag_map")
      .select("legacy_name")
      .ilike("legacy_name", slug.replace(/-/g, " "));
    const names = ((legacy ?? []) as any[]).map((r) => r.legacy_name).filter(Boolean);
    if (names.length === 0) return 0;

    const { count } = await (sb as any)
      .from("cdn_products")
      .select("product_id", { count: "exact", head: true })
      .in("generic_legacy_name", names)
      .eq("is_active", true)
      .eq("is_published", true);
    return typeof count === "number" ? count : null;
  } catch {
    return null;
  }
}
export type PartsSeo = {
  title: string;
  description: string;
  slug: string;
  image: string | null;
  images: string[];
  brand: string | null;
  sku: string;
  mpn: string | null;
  gtin: string | null;
  category: string | null;
  price: number | null;
  inStock: boolean;
};

/** Server-renderable product facts so crawlers (Google Merchant "Found by Google")
 *  see the price and identifiers in the first HTML response. */
export async function loadPartsSeo(key: string): Promise<PartsSeo | null> {
  if (!isSafePartsKey(key)) return null;
  try {
    const sb = await getSupabase();
    const { data: r } = await (sb as any)
      .from("cdn_products")
      .select(
        "product_id, product_number, short_slug, admin_slug, product_title, admin_title, admin_meta_title, meta_title, admin_meta_description, meta_description, description, vendor_name, part_type_name, upc, admin_gtin, admin_mpn, manufacturer_part_no, admin_sale_price, admin_price, map_retail, customer_retail_price, customer_cost, inventory_total, admin_stock_quantity, admin_images, image_1_url, image_2_url, image_3_url",
      )
      .or(`short_slug.eq.${key},admin_slug.eq.${key}`)
      .limit(1)
      .maybeSingle();
    if (!r) return null;
    const num = (v: unknown) => {
      const n = v == null || v === "" ? NaN : Number(v);
      return Number.isFinite(n) && n > 0 ? n : null;
    };
    const price =
      partsSellPriceRow(r);
    const adminImgs: string[] = Array.isArray(r.admin_images) ? r.admin_images.filter(Boolean) : [];
    const images = (adminImgs.length ? adminImgs : [r.image_1_url, r.image_2_url, r.image_3_url]).filter(
      (u: any) => typeof u === "string" && /^https?:\/\//.test(u),
    );
    const gtinRaw = String(r.admin_gtin || r.upc || "").replace(/\D/g, "");
    const gtin = [8, 12, 13, 14].includes(gtinRaw.length) ? gtinRaw : null;
    const title = r.admin_title || r.product_title || String(r.product_number || r.product_id);
    const desc = String(r.admin_meta_description || r.meta_description || r.description || title)
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 300);
    const qty = Number(r.admin_stock_quantity ?? r.inventory_total);
    return {
      title,
      description: desc,
      slug: r.short_slug || r.admin_slug || key,
      image: images[0] ?? null,
      images: images.slice(0, 6),
      brand: r.vendor_name || null,
      sku: String(r.product_number || r.product_id),
      mpn: r.admin_mpn || r.manufacturer_part_no || r.product_number || null,
      gtin,
      category: r.part_type_name || null,
      price,
      inStock: Number.isFinite(qty) ? qty > 0 : true,
    };
  } catch {
    return null;
  }
}
